/**
 * Ожидание готовности FILEATTACHMENTS к getFullData после close Р7 (Swing saveIfNeed).
 * Poll DS-фазы: awaiting_ws/downloading → staged|idle; error — сразу.
 * С `onReconcile` — POST document-sync-state каждые reconcileMs; cap — maxWaitMs.
 * `onForceSaveRetry` опционален (тесты); form-save вызывает Command Service один раз до wait.
 */
import type { FileItem } from '@/common/types/fileItem'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import {
  isAttachmentR7EditBlocked,
  type FileAttachmentsElementMeta,
} from '@/common/utils/attachmentRpcPolicy'
import {
  findSessionForAttachmentItem,
  resolveR7CallbackWaitMs,
} from '@/common/utils/r7AttachmentEditSession'

export type R7FormSaveWaitResult = 'ready' | 'timeout' | 'error'

/** Form-save poll `document-sync-state`: `stale`/`syncing` are in-flight, not abort. */
export function isFormSaveDocumentSyncStateTerminal(status: {
  syncState?: string | null
  dsStatus?: number | null
}): boolean {
  if (status.syncState === 'error') return true
  const ds = status.dsStatus
  return ds === 3 || ds === 7
}

export function classifyFormSaveDocumentSyncState(status: {
  syncState?: string | null
  dsStatus?: number | null
}): 'ready' | 'keep_waiting' | 'error' {
  if (isFormSaveDocumentSyncStateTerminal(status)) return 'error'
  if (status.syncState === 'ready') return 'ready'
  return 'keep_waiting'
}

/** Destroy iframe only after wait `ready` and session is staged or clean. */
export function shouldDestroyR7EditorAfterFormSaveWait(
  waitResult: R7FormSaveWaitResult,
  session: { phase?: string; wasDirty?: boolean } | null | undefined,
): boolean {
  if (waitResult !== 'ready') return false
  if (!session || session.phase === 'error') return false
  return session.phase === 'staged' || session.wasDirty === false
}

/** timeout/error must abort getFullData — do not continue the form action silently. */
export function r7FormSaveWaitBlocksGetFullData(result: R7FormSaveWaitResult): boolean {
  return result === 'timeout' || result === 'error'
}

export type WaitUntilAttachmentsR7NotBlockingOptions = {
  items: readonly FileItem[]
  meta: FileAttachmentsElementMeta
  elementId: number | null
  timeoutMs?: number
  pollMs?: number
  /** How often to call `onReconcile` (default 1500ms). */
  reconcileMs?: number
  /** Cap when `onReconcile` / `onForceSaveRetry` is set (default timeoutMs * 3). */
  maxWaitMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
  onReconcile?: () => Promise<void>
  /** Form-save: re-issue DocEditor `forcesave` while the iframe is still alive. */
  onForceSaveRetry?: () => void
}

function isR7FormSaveWaitError(item: FileItem, elementId: number | null): boolean {
  const eid = elementId != null ? Number(elementId) : NaN
  if (Number.isFinite(eid) && eid > 0) {
    try {
      if (getDsAttachmentStore(eid).getPhase(item) === 'error') return true
    } catch {
      /* store может отсутствовать в тестах без Vue app */
    }
  }
  return findSessionForAttachmentItem(item, elementId)?.phase === 'error'
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

export type WaitUntilAttachmentPreviewReadyForFormSaveOptions = {
  isPreviewLoading: () => boolean
  hasUploadableNativeFile: () => boolean
  timeoutMs?: number
  pollMs?: number
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

/**
 * Form-save во время inline preview: native File → сразу upload, preview не abort.
 * Без File — ждать конец loading, не silent `code=-1`.
 */
export async function waitUntilAttachmentPreviewReadyForFormSave(
  options: WaitUntilAttachmentPreviewReadyForFormSaveOptions,
): Promise<R7FormSaveWaitResult> {
  const timeoutMs = options.timeoutMs ?? resolveR7CallbackWaitMs()
  const pollMs = options.pollMs ?? 50
  const now = options.now ?? Date.now
  const sleep = options.sleep ?? defaultSleep

  if (options.hasUploadableNativeFile()) return 'ready'
  if (!options.isPreviewLoading()) return 'ready'

  const start = now()
  while (options.isPreviewLoading()) {
    if (options.hasUploadableNativeFile()) return 'ready'
    if (now() - start >= timeoutMs) return 'timeout'
    await sleep(pollMs)
  }
  return 'ready'
}

/**
 * Ждёт, пока ни одна строка не блокирует getFullData (`awaiting_ws` / `downloading` / dirty editing).
 * `error` — сразу (DS phase / session). Form-save reconcile: stale/syncing keep polling.
 */
export async function waitUntilAttachmentsR7NotBlockingGetFullData(
  options: WaitUntilAttachmentsR7NotBlockingOptions,
): Promise<R7FormSaveWaitResult> {
  const timeoutMs = options.timeoutMs ?? resolveR7CallbackWaitMs()
  const pollMs = options.pollMs ?? 50
  const reconcileMs = options.reconcileMs ?? 1500
  const hasReconcileLoop = Boolean(options.onReconcile || options.onForceSaveRetry)
  const capMs = hasReconcileLoop
    ? (options.maxWaitMs ?? timeoutMs * 3)
    : timeoutMs
  const now = options.now ?? Date.now
  const sleep = options.sleep ?? defaultSleep
  const start = now()
  let lastReconcile = start

  while (true) {
    const blocked = options.items.filter((item) =>
      isAttachmentR7EditBlocked(item, options.meta, options.elementId),
    )
    if (blocked.length === 0) return 'ready'
    if (blocked.some((item) => isR7FormSaveWaitError(item, options.elementId))) {
      return 'error'
    }
    const elapsed = now() - start
    if (elapsed >= capMs) return 'timeout'
    if (hasReconcileLoop && now() - lastReconcile >= reconcileMs) {
      options.onForceSaveRetry?.()
      if (options.onReconcile) await options.onReconcile()
      lastReconcile = now()
      continue
    }
    await sleep(pollMs)
  }
}
